'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, Command, ArrowRight, Bot, Calendar, BarChart3, Home, FileCheck, Radio, Brain, Monitor, Beaker, MessageSquare } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface PaletteItem {
    id: string;
    label: string;
    desc?: string;
    href: string;
    icon: React.ReactNode;
    category: 'page' | 'agent' | 'action';
}

const ITEMS: PaletteItem[] = [
    // Pages
    { id: 'home', label: '홈', desc: '메인 페이지', href: '/', icon: <Home className="w-4 h-4" />, category: 'page' },
    { id: 'scheduler', label: '스케줄러', desc: '공정 관리', href: '/scheduler', icon: <Calendar className="w-4 h-4" />, category: 'page' },
    { id: 'analytics', label: '분석', desc: '데이터 분석', href: '/analytics', icon: <BarChart3 className="w-4 h-4" />, category: 'page' },
    { id: 'live', label: 'LIVE', desc: 'SnapAgent LIVE', href: '/live', icon: <Radio className="w-4 h-4" />, category: 'page' },
    { id: 'grid', label: '그리드', desc: '인프라 시각화', href: '/grid', icon: <Monitor className="w-4 h-4" />, category: 'page' },
    { id: 'approval', label: '결재', desc: '결재 시스템', href: '/approval', icon: <FileCheck className="w-4 h-4" />, category: 'page' },
    { id: 'demo', label: '데모', desc: '데모 페이지', href: '/demo', icon: <Beaker className="w-4 h-4" />, category: 'page' },
    { id: 'chat', label: 'RAG 챗', desc: 'AI 채팅', href: '/chat', icon: <MessageSquare className="w-4 h-4" />, category: 'page' },
    { id: 'usage', label: '사용량', desc: '사용량 대시보드', href: '/usage', icon: <BarChart3 className="w-4 h-4" />, category: 'page' },
    { id: 'ptk', label: 'A프로젝트', desc: '실증 프로젝트', href: '/ptk', icon: <Brain className="w-4 h-4" />, category: 'page' },
    { id: 'pricing', label: '요금제', desc: '서비스 요금', href: '/pricing', icon: <BarChart3 className="w-4 h-4" />, category: 'page' },
    // Agents
    { id: 'boq', label: 'BOQ Agent', desc: '견적 분석', href: '/boq-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'cost', label: 'Cost Agent', desc: '원가 관리', href: '/cost-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'btl', label: 'BTL Agent', desc: '공공건축', href: '/btl-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'design', label: 'Design Agent', desc: '설계 검토', href: '/design-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'bf', label: 'BF Agent', desc: 'BF 인증', href: '/bf-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'cad', label: 'CAD Agent', desc: '도면 분석', href: '/cad-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'ppt', label: 'PPT Agent', desc: 'PPT 생성', href: '/ppt-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'color', label: 'Color Agent', desc: '색채 분석', href: '/color-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'ren', label: 'Render Agent', desc: '렌더링', href: '/ren-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'pay', label: 'Pay Agent', desc: '기성 관리', href: '/pay-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
    { id: 'skp', label: 'SKP Agent', desc: 'SketchUp', href: '/skp-agent', icon: <Bot className="w-4 h-4" />, category: 'agent' },
];

export function CommandPalette() {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedIdx, setSelectedIdx] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const router = useRouter();

    // Cmd+K / Ctrl+K
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(o => !o);
            }
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    useEffect(() => {
        if (open) {
            setQuery('');
            setSelectedIdx(0);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    const filtered = query
        ? ITEMS.filter(item =>
            item.label.toLowerCase().includes(query.toLowerCase()) ||
            item.desc?.toLowerCase().includes(query.toLowerCase()) ||
            item.id.includes(query.toLowerCase())
        )
        : ITEMS;

    const navigate = useCallback((href: string) => {
        setOpen(false);
        router.push(href);
    }, [router]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIdx(i => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIdx(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && filtered[selectedIdx]) {
            navigate(filtered[selectedIdx].href);
        }
    }, [filtered, selectedIdx, navigate]);

    useEffect(() => { setSelectedIdx(0); }, [query]);

    const grouped = {
        page: filtered.filter(i => i.category === 'page'),
        agent: filtered.filter(i => i.category === 'agent'),
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9990] bg-black/60 backdrop-blur-sm"
                        onClick={() => setOpen(false)}
                    />
                    {/* Palette */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -20 }}
                        transition={{ duration: 0.15 }}
                        className="fixed top-[15%] left-1/2 -translate-x-1/2 z-[9991] w-full max-w-lg"
                    >
                        <div className="bg-gray-950/95 backdrop-blur-2xl border border-white/15 rounded-2xl shadow-2xl overflow-hidden">
                            {/* Search Input */}
                            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                                <Search className="w-5 h-5 text-gray-500 shrink-0" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="페이지, 에이전트 검색..."
                                    className="flex-1 bg-transparent text-white text-sm placeholder:text-gray-500 outline-none"
                                />
                                <kbd className="text-[10px] text-gray-500 px-1.5 py-0.5 rounded border border-white/10 bg-white/5">ESC</kbd>
                            </div>
                            {/* Results */}
                            <div className="max-h-[360px] overflow-y-auto p-2">
                                {filtered.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500 text-sm">
                                        &quot;{query}&quot; 검색 결과 없음
                                    </div>
                                ) : (
                                    <>
                                        {grouped.page.length > 0 && (
                                            <>
                                                <div className="text-[10px] font-bold text-gray-500 px-3 py-1.5 uppercase">페이지</div>
                                                {grouped.page.map((item, i) => {
                                                    const globalIdx = filtered.indexOf(item);
                                                    return (
                                                        <button key={item.id} onClick={() => navigate(item.href)}
                                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer ${globalIdx === selectedIdx ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'}`}>
                                                            <span className="text-gray-500">{item.icon}</span>
                                                            <span className="font-medium">{item.label}</span>
                                                            {item.desc && <span className="text-gray-600 text-xs ml-auto">{item.desc}</span>}
                                                            {globalIdx === selectedIdx && <ArrowRight className="w-3.5 h-3.5 text-gray-500 ml-1" />}
                                                        </button>
                                                    );
                                                })}
                                            </>
                                        )}
                                        {grouped.agent.length > 0 && (
                                            <>
                                                <div className="text-[10px] font-bold text-gray-500 px-3 py-1.5 uppercase mt-1">AI 에이전트</div>
                                                {grouped.agent.map((item) => {
                                                    const globalIdx = filtered.indexOf(item);
                                                    return (
                                                        <button key={item.id} onClick={() => navigate(item.href)}
                                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer ${globalIdx === selectedIdx ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/5'}`}>
                                                            <span className="text-violet-400">{item.icon}</span>
                                                            <span className="font-medium">{item.label}</span>
                                                            {item.desc && <span className="text-gray-600 text-xs ml-auto">{item.desc}</span>}
                                                            {globalIdx === selectedIdx && <ArrowRight className="w-3.5 h-3.5 text-gray-500 ml-1" />}
                                                        </button>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                            {/* Footer */}
                            <div className="px-4 py-2 border-t border-white/10 flex items-center gap-4 text-[10px] text-gray-600">
                                <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border border-white/10 bg-white/5">↑↓</kbd> 이동</span>
                                <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border border-white/10 bg-white/5">↵</kbd> 열기</span>
                                <span className="flex items-center gap-1 ml-auto"><Command className="w-3 h-3" /><kbd className="px-1 py-0.5 rounded border border-white/10 bg-white/5">K</kbd> 토글</span>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
